import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Card, Button, TextField, InputAdornment, Chip, IconButton, MenuItem, Typography, FormControl, InputLabel, Select, Avatar, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import ErpDataGrid from '../../components/common/ErpDataGrid';
import { Add as AddIcon, Search as SearchIcon, Edit as EditIcon, Delete as DeleteIcon, Visibility as ViewIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

const EmployeesList = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ employment_type: '', status: '' });
  const [pagination, setPagination] = useState({ page: 0, pageSize: 10, total: 0 });
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(false);

  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/employees', { params: { page: pagination.page + 1, limit: pagination.pageSize, search, ...filters } });
      setEmployees(response.data.data);
      setPagination(prev => ({ ...prev, total: response.data.pagination.total }));
    } catch (error) {
      console.error(error);
      const msg = error.response?.data?.message || error.message || 'Failed to load employees';
      toast.error(msg);
    }
    finally { setLoading(false); }
  }, [pagination.page, pagination.pageSize, search, filters]);

  useEffect(() => { fetchEmployees(); }, [fetchEmployees]);

  const handleDelete = async () => {
    if (!selectedEmployee) return;
    try {
      await api.delete(`/employees/${selectedEmployee.id}`);
      toast.success('Employee deleted');
      fetchEmployees();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to delete'); }
    setDeleteDialog(false);
    setSelectedEmployee(null);
  };

  const typeColors = { fulltime: '#10b981', parttime: '#3b82f6', contractor: '#f59e0b', intern: '#8b5cf6' };
  const statusColors = {
    active: '#10b981',
    inactive: '#6b7280',
    terminated: '#ef4444',
    resigned: '#f59e0b',
    pending: '#6366f1',
    no_profile: '#94a3b8',
  };

  const goToRow = (row) => {
    if (row.needsEmployeeProfile) {
      navigate(`/employees/new?contact_id=${row.contact_id}`);
    } else {
      navigate(`/employees/${row.id}`);
    }
  };

  const columns = [
    {
      field: 'employee_code',
      headerName: 'Reference',
      minWidth: 200,
      flex: 0.45,
      renderCell: (p) =>
        p.row.needsEmployeeProfile ? (
          <Chip label="Add profile" size="small" color="warning" variant="outlined" />
        ) : (
          <Tooltip title={p.value || '—'}>
            <Typography
              variant="body2"
              sx={{
                fontFamily: 'ui-monospace, monospace',
                fontSize: '0.8125rem',
                fontWeight: 600,
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                lineHeight: 1.35,
                py: 0.5,
              }}
            >
              {p.value || '—'}
            </Typography>
          </Tooltip>
        ),
    },
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 200, renderCell: (params) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36, fontSize: '0.9rem' }}>{params.row.Contact?.first_name?.[0]}{params.row.Contact?.last_name?.[0]}</Avatar>
        <Box><Typography variant="body2" sx={{ fontWeight: 500 }}>{params.row.Contact?.first_name} {params.row.Contact?.last_name}</Typography><Typography variant="caption" color="text.secondary">{params.row.Contact?.email}</Typography></Box>
      </Box>
    )},
    { field: 'job_title', headerName: 'Job title', width: 150, valueGetter: (params) => params.row.job_title || params.row.designation || '—' },
    { field: 'team_name', headerName: 'Team', width: 120, valueGetter: (params) => params.row.team_name || '—' },
    { field: 'department', headerName: 'Department', width: 120 },
    { field: 'reportingManager', headerName: 'Reports to', width: 160, valueGetter: (params) => {
      const rm = params.row.reportingManager;
      if (!rm) return '—';
      return `${rm.first_name || ''} ${rm.last_name || ''}`.trim() || rm.email;
    } },
    { field: 'employment_type', headerName: 'Type', width: 110, renderCell: (params) => (params.row.needsEmployeeProfile ? <Typography variant="caption" color="text.secondary">—</Typography> : <Chip label={params.value?.replace('_', ' ').toUpperCase()} size="small" sx={{ bgcolor: `${typeColors[params.value]}20`, color: typeColors[params.value], fontWeight: 500, fontSize: '0.7rem' }} />) },
    { field: 'status', headerName: 'Status', width: 130, renderCell: (params) => {
      if (params.row.needsEmployeeProfile) {
        return (
          <Chip
            label="NO HR PROFILE"
            size="small"
            sx={{ bgcolor: `${statusColors.no_profile}20`, color: statusColors.no_profile, fontWeight: 500, fontSize: '0.65rem' }}
          />
        );
      }
      const v = params.value;
      return (
        <Chip
          label={String(v || '—').toUpperCase()}
          size="small"
          sx={{ bgcolor: `${statusColors[v]}20`, color: statusColors[v] || '#64748b', fontWeight: 500, fontSize: '0.7rem' }}
        />
      );
    } },
    { field: 'actions', headerName: 'Actions', width: 150, sortable: false, renderCell: (params) => (
      <Box sx={{ display: 'flex', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
        <Tooltip title={params.row.needsEmployeeProfile ? 'Add HR profile' : 'View'}><IconButton size="small" onClick={() => goToRow(params.row)}><ViewIcon fontSize="small" /></IconButton></Tooltip>
        {isAdmin && !params.row.needsEmployeeProfile && <Tooltip title="Edit"><IconButton size="small" onClick={() => navigate(`/employees/${params.row.id}/edit`)}><EditIcon fontSize="small" /></IconButton></Tooltip>}
        {isAdmin && !params.row.needsEmployeeProfile && <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => { setSelectedEmployee(params.row); setDeleteDialog(true); }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>}
      </Box>
    ) }
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Employees
          </Typography>
          <Typography variant="body2" color="text.secondary">
            HR profiles for contacts; each employee is associated with a <strong>Hosting Company</strong> account (company
            hosting this tenant). Use Accounts → add type <strong>Hosting Company *</strong> if you need more host orgs.
          </Typography>
        </Box>
        {isAdmin && (<Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/employees/new')} sx={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}>Add Employee</Button>)}
      </Box>

      <Card sx={{ mb: 3, p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField placeholder="Search employees..." value={search} onChange={(e) => setSearch(e.target.value)} size="small" sx={{ minWidth: 300 }} InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon sx={{ color: 'text.secondary' }} /></InputAdornment>) }} />
          <FormControl size="small" sx={{ minWidth: 150 }}><InputLabel>Type</InputLabel><Select value={filters.employment_type} label="Type" onChange={(e) => setFilters({ ...filters, employment_type: e.target.value })}><MenuItem value="">All</MenuItem><MenuItem value="fulltime">Full Time</MenuItem><MenuItem value="parttime">Part Time</MenuItem><MenuItem value="contractor">Contractor</MenuItem></Select></FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}><InputLabel>Status</InputLabel><Select value={filters.status} label="Status" onChange={(e) => setFilters({ ...filters, status: e.target.value })}><MenuItem value="">All</MenuItem><MenuItem value="pending">Pending approval</MenuItem><MenuItem value="active">Active</MenuItem><MenuItem value="inactive">Inactive</MenuItem><MenuItem value="terminated">Terminated</MenuItem></Select></FormControl>
        </Box>
      </Card>

      <Card>
        <ErpDataGrid
          rows={employees}
          columns={columns}
          loading={loading}
          paginationMode="server"
          rowCount={pagination.total}
          paginationModel={{ page: pagination.page, pageSize: pagination.pageSize }}
          onPaginationModelChange={(model) => setPagination({ ...pagination, ...model })}
          pageSizeOptions={[10, 25, 50]}
          getRowId={(row) => String(row.id)}
          getRowHeight={() => 'auto'}
          disableRowSelectionOnClick
          autoHeight
          sx={{
            '& .MuiDataGrid-row': { cursor: 'pointer' },
            '& .MuiDataGrid-cell': { alignItems: 'flex-start', py: 0.75 },
          }}
          onRowClick={(params) => goToRow(params.row)}
        />
      </Card>

      <Dialog open={deleteDialog} onClose={() => { setDeleteDialog(false); setSelectedEmployee(null); }}>
        <DialogTitle>Delete Employee</DialogTitle>
        <DialogContent><Typography>Are you sure you want to delete this employee? This cannot be undone.</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => { setDeleteDialog(false); setSelectedEmployee(null); }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmployeesList;
